/* eslint-disable no-unused-expressions */
import { Option } from '@carvana/showroom-autocomplete';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { StyledAutocompleteStreet } from './address-next.styles';

const AutocompleteStreet = props => {
  const {
    type,
    error,
    touched,
    onBlur,
    setFieldValue,
    streetValue,
    isDisabled,
    setFieldTouched,
    primaryStreetValue,
    setCosignerState,
    setStreetAutocompleteState,
    setCityAutocompleteState,
    setZipAutocompleteState,
    isCosigner,
    hideErrorMessage
  } = props;
  const [options, setOptions] = useState([]);
  const [optionSelected, setOptionSelected] = useState(null);
  const autoCompleteStreetAddressRef = useRef(null);

  const handleStreetChange = newValue => {
    const endpoint = `${process.env.CARVANA_ADDRESS_API_URL}/v1/address/suggest`;
    if (optionSelected && options.includes(optionSelected)) {
      setOptions([]);
      return;
    }
    if (newValue !== '') {
      setOptionSelected(null);
      axios
        .get(endpoint, { params: { partialStreetAddress: newValue } })
        .then(res => {
          const updatedOptions = [];
          res.data.content.forEach(addressGroup => {
            const option = {
              addressGroup,
              fullSuggestedAddress: `${addressGroup.street}${
                addressGroup.street2 && ` ${addressGroup.street2}`
              }, ${addressGroup.city}, ${addressGroup.state} ${
                addressGroup.zip5
              }`
            };
            updatedOptions.push(option);
          });
          setOptions(updatedOptions);
        });
    }
    setFieldValue(`${type}Street`, newValue);
    setStreetAutocompleteState('manual');
  };

  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;

  const handleSelection = async selectionValue => {
    nativeInputValueSetter.call(
      autoCompleteStreetAddressRef.current,
      selectionValue.addressGroup.street
    );

    const event = new Event('input', { bubbles: true });
    autoCompleteStreetAddressRef.current.dispatchEvent(event);
    setOptionSelected(selectionValue);

    await setFieldValue(`${type}Street`, selectionValue.addressGroup.street);
    await setFieldValue(
      `${type}Street2`,
      selectionValue.addressGroup.street2 || ''
    );
    await setFieldValue(`${type}City`, selectionValue.addressGroup.city);
    if (!isCosigner)
      await setFieldValue(`${type}State`, selectionValue.addressGroup.state);
    await setFieldValue(`${type}Zip`, selectionValue.addressGroup.zip5);

    setFieldTouched(`${type}City`, true);
    setFieldTouched(`${type}State`, true);
    setFieldTouched(`${type}Zip`, true);

    setStreetAutocompleteState('autocomplete');
    setCityAutocompleteState('autocomplete');
    setZipAutocompleteState('autocomplete');

    if (isCosigner) {
      setFieldValue(`coAutocompleteState`, selectionValue.addressGroup.state);
      setCosignerState(selectionValue.addressGroup.state);
    }
  };

  useEffect(() => {
    if (
      isCosigner &&
      (primaryStreetValue || primaryStreetValue === '') &&
      nativeInputValueSetter
    ) {
      nativeInputValueSetter.call(
        autoCompleteStreetAddressRef.current,
        primaryStreetValue
      );

      const event = new Event('input', { bubbles: true });

      if (autoCompleteStreetAddressRef && autoCompleteStreetAddressRef.current)
        autoCompleteStreetAddressRef.current.dispatchEvent(event);

      setOptions([]);
    }
  }, [primaryStreetValue, nativeInputValueSetter, isCosigner]);

  return (
    <StyledAutocompleteStreet
      id={`${type}Street`}
      name={`${type}Street`}
      changeCallback={handleStreetChange}
      onSelection={handleSelection}
      onBlur={onBlur}
      initialInputValue={streetValue}
      label={isCosigner ? 'Home Address' : 'Address, Line 1'}
      debounceDelay={250}
      hasSearchIcons={false}
      ref={autoCompleteStreetAddressRef}
      type="input"
      error={error}
      touched={touched}
      disabled={isDisabled || false}
      isCosigner={isCosigner}
      hideErrorMessage={hideErrorMessage}
      autoComplete="none"
      {...props}
    >
      {options.map(opt => (
        <Option value={opt} key={opt.fullSuggestedAddress}>
          {opt.fullSuggestedAddress}
        </Option>
      ))}
    </StyledAutocompleteStreet>
  );
};

AutocompleteStreet.propTypes = {
  onBlur: PropTypes.func,
  touched: PropTypes.bool,
  isDisabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  type: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  streetValue: PropTypes.string,
  setFieldTouched: PropTypes.func.isRequired,
  primaryStreetValue: PropTypes.string,
  isCosigner: PropTypes.bool,
  setCosignerState: PropTypes.func,
  setStreetAutocompleteState: PropTypes.func,
  setCityAutocompleteState: PropTypes.func,
  setZipAutocompleteState: PropTypes.func
};

AutocompleteStreet.defaultProps = {
  onBlur: () => {},
  touched: false,
  error: false,
  isDisabled: false,
  type: '',
  streetValue: '',
  primaryStreetValue: '',
  isCosigner: false,
  setCosignerState: () => {},
  setStreetAutocompleteState: () => {},
  setCityAutocompleteState: () => {},
  setZipAutocompleteState: () => {}
};

export default AutocompleteStreet;
